import React from "react";
import Popper from "@ui/Popper";
import { css } from "@linaria/core";
import { colors, spacings, shadows } from "@ui/Theme";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import Text from "@ui/Text";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import Checkbox from "@ui/Checkbox";
import Button from "@ui/Button";
import { ShareBoxLine } from "@ui/IconV2/react";
import t from "../../../../../i18n";
import {
  useCreateRemoteDashboardCallback,
  type Dashboard,
} from "../../../Dashboards/hook/api/dashboard";
import { filterPublishedDashboards } from "../../../Dashboards/utils/search";
import { usePost } from "../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import type { SummaryDashboard } from "../utils";
import { useTranslations } from "../../../../../api/useTranslations";
import { toPath } from "../../../../../router/helpers/path";
import { toBasenamePath } from "../../../../../router/helpers/navigation";

interface Props {
  children: React.ReactElement;
  projectId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  publishedDashboards: Dashboard[];
  summaryDashboards: SummaryDashboard[];
  setSummaryDashboards: React.Dispatch<React.SetStateAction<SummaryDashboard[]>>;
  summaryPos: number;
}

const DashboardPopup = ({
  children,
  projectId,
  open,
  setOpen,
  publishedDashboards,
  summaryDashboards,
  setSummaryDashboards,
  summaryPos,
}: Props) => {
  const translations = useTranslations();
  const [search, setSearch] = React.useState<string>("");
  const checkboxRefs = React.useRef<{ [key: string]: HTMLInputElement }>({});
  const lockRemoteDashboard = usePost(
    "/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)/locks",
  );

  const linkedIds = React.useMemo(
    () => new Set(summaryDashboards.map(sd => sd.dashboardId)),
    [summaryDashboards],
  );

  const maxPosition = React.useMemo(
    () =>
      summaryDashboards.length > 0 ? Math.max(...summaryDashboards.map(sd => sd.position)) : 0,
    [summaryDashboards],
  );

  const getNextPosition = React.useCallback(
    () => (summaryPos >= maxPosition ? summaryPos + 1 : maxPosition + 1),
    [summaryPos, maxPosition],
  );

  const { addToast } = useToasts();
  const createSummaryDashboard = usePost("/projects/:projectId(uuid)/summary/dashboards");
  const createRemoteDashboard = useCreateRemoteDashboardCallback();

  const isLinked = React.useCallback(
    (dashboardId: string) => linkedIds.has(dashboardId),
    [linkedIds],
  );

  const searchResults = React.useMemo(
    () =>
      filterPublishedDashboards(publishedDashboards, search).sort((a, b) =>
        a.title.localeCompare(b.title),
      ),
    [publishedDashboards, search],
  );

  const selectDashboard = React.useCallback(
    (dashboardId: string) => {
      if (linkedIds.has(dashboardId)) return;

      const newPosition = getNextPosition();
      const newLink: SummaryDashboard = {
        id: `${projectId}-${dashboardId}`,
        projectId,
        dashboardId,
        position: newPosition,
      };

      const prevOccupants = [...summaryDashboards];

      setSummaryDashboards(prev => [...prev, newLink]);

      createSummaryDashboard({
        routeParams: { projectId },
        body: { dashboardId, position: newPosition },
      }).catch(() => {
        addToast("error", t("errors.backend_error"));
        setSummaryDashboards(prevOccupants);
      });
    },
    [
      linkedIds,
      getNextPosition,
      projectId,
      summaryDashboards,
      setSummaryDashboards,
      createSummaryDashboard,
      addToast,
    ],
  );

  const createPublishAndLinkDashboard = React.useCallback(async () => {
    const prevOccupants = [...summaryDashboards];

    try {
      const dashboard = {
        projectId,
        title: t("project.dashboard.title.empty"),
        published: true,
        readonly: false,
        description: null,
      };

      const result = await createRemoteDashboard(dashboard);
      if (!result) throw new Error(t("errors.backend_error"));

      const dashboardId = result.dashboard.id;
      const newPosition = getNextPosition();
      const newLink: SummaryDashboard = {
        id: `${projectId}-${dashboardId}`,
        projectId,
        dashboardId,
        position: newPosition,
      };

      setSummaryDashboards(prev => [...prev, newLink]);

      await createSummaryDashboard({
        routeParams: { projectId },
        body: { dashboardId, position: newPosition },
      });
      await lockRemoteDashboard({ routeParams: { projectId, dashboardId: result.dashboard.id } });
      window.open(
        toBasenamePath(
          toPath("/projects/:projectId/dashboards/:dashboardId/edit", {
            projectId,
            dashboardId,
          }),
        ),
      );
      setSearch("");
      setOpen(false);
    } catch (err) {
      setSummaryDashboards(prevOccupants);
      if (err instanceof Error) addToast("error", err.message);
      else addToast("error", t("errors.backend_error"));
    }
  }, [
    summaryDashboards,
    projectId,
    createRemoteDashboard,
    getNextPosition,
    setSummaryDashboards,
    createSummaryDashboard,
    lockRemoteDashboard,
    setOpen,
    addToast,
  ]);

  return (
    <Popper
      opened={open}
      setOpened={setOpen}
      click
      className={dashboardSelect}
      closeOnOutsideClick
      placement="bottom-start"
      gap={4}
      overlay={
        <div className={dashboardSelectOverlay}>
          <header>
            <SearchInputV2
              className={dashboardSelectSearch}
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </header>
          <div>
            {publishedDashboards.length === 0 ? (
              <Text type="body2" className={dashboardSelectMessage}>
                {t("summary.dashboard.popup.empty", translations)}
              </Text>
            ) : searchResults.length === 0 ? (
              <Text type="body2" className={dashboardSelectMessage}>
                {t("common.search.no_result")}
              </Text>
            ) : (
              searchResults.map(dashboard => (
                <OptionV2
                  key={dashboard.id}
                  onClick={() => checkboxRefs.current[dashboard.id]?.click()}
                  disabled={isLinked(dashboard.id)}
                  withTextEllipsis
                >
                  <Checkbox
                    ref={el => el && (checkboxRefs.current[dashboard.id] = el)}
                    slot="checkbox"
                    smallIndicator
                    checked={isLinked(dashboard.id)}
                    onChange={() => selectDashboard(dashboard.id)}
                  />
                  <FragmentSlot slot="text">{dashboard.title}</FragmentSlot>
                  <FragmentSlot slot="description">{dashboard.description}</FragmentSlot>
                </OptionV2>
              ))
            )}
          </div>
          <hr />

          <footer>
            <Button
              variant="link"
              leftIcon={<ShareBoxLine />}
              slim
              className={dashboardSelectCreateShortcut}
              onClick={createPublishAndLinkDashboard}
            >
              {t("summary.dashboard.popup.create", translations)}
            </Button>
          </footer>
        </div>
      }
    >
      {children}
    </Popper>
  );
};

export default DashboardPopup;

//#region CSS
const dashboardSelect = css`
  border-radius: 4px;
  box-shadow: ${shadows.shadow100};
  width: 440px;
`;

const dashboardSelectOverlay = css`
  border-radius: 4px;
  max-height: min(40vh, 450px);
  overflow-y: auto;
  position: relative;

  > :where(header, div, footer) {
    padding: ${spacings.s};
    background: ${colors.hydrogen};
  }

  > :where(header, footer) {
    position: sticky;
    z-index: 1;
  }

  > header {
    top: 0;
  }

  > footer {
    bottom: 0;
  }

  > hr {
    background: ${colors.titanium100};
    border: none;
    height: 1px;
    margin: 0 ${spacings.s};
  }
`;

const dashboardSelectSearch = css`
  width: 100%;
`;

const dashboardSelectCreateShortcut = css`
  display: block;
  margin: auto;
`;

const dashboardSelectMessage = css`
  color: ${colors.carbon700};
  text-align: center;
`;
//#endregion CSS
